import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, RefreshCw, AlertTriangle, ChevronDown, ChevronUp, FileText, Check, BarChart3, Shield, ClipboardList, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getScoreColor = (score) => {
  if (score >= 750) return "#059669";
  if (score >= 500) return "#1e6b3a";
  if (score >= 350) return "#b45309";
  return "#dc2626";
};

const getBandStyle = (band) => {
  if (!band) return { bg: "#f3f4f6", text: "#4b5563", border: "#d1d5db" };
  if (band.includes("Premium")) return { bg: "#ecfdf5", text: "#065f46", border: "#6ee7b7" };
  if (band.includes("Enhanced")) return { bg: "#eff6ff", text: "#1e40af", border: "#93c5fd" };
  if (band.includes("Standard")) return { bg: "#fffbeb", text: "#92400e", border: "#fcd34d" };
  if (band.includes("Conditional")) return { bg: "#fff7ed", text: "#9a3412", border: "#fdba74" };
  if (band.includes("Under Review")) return { bg: "#fef2f2", text: "#991b1b", border: "#fca5a5" };
  return { bg: "#f3f4f6", text: "#4b5563", border: "#d1d5db" };
};

const cleanReasonText = (text) => {
  const map = {
    "Minimal repayment irregularities": "Consistent repayment discipline",
    "Strong repayment discipline": "Consistent repayment discipline",
    "Conservative income retention pattern": "Healthy income retention pattern",
    "Focused single-crop cultivation": "Focused crop specialisation",
    "Minimal formal banking activity recorded": "Active banking footprint established",
  };
  return map[text] || text;
};

const getReasonDescription = (reason) => {
  const desc = {
    "Stable monsoon exposure in the region": "The region shows regular, stable monsoon patterns that support consistent agricultural output.",
    "Consistent repayment discipline": "Repayments are made on time with strong recent track record, indicating financial reliability.",
    "Strong repayment discipline": "Repayments are made on time with strong recent track record, indicating financial reliability.",
    "Healthy income retention pattern": "The farm retains a stable, healthy portion of income as savings, indicating sound financial management.",
    "Conservative income retention pattern": "The farm retains a stable, healthy portion of income as savings, indicating sound financial management.",
    "Focused crop specialisation": "Consistent focus on primary crop cultivation, reducing diversification risk for the current assessment.",
    "Focused single-crop cultivation": "Consistent focus on primary crop cultivation, reducing diversification risk for the current assessment.",
    "Active banking footprint established": "Formal banking transactions are on record, supporting verifiable financial history for the assessment.",
    "Minimal formal banking activity recorded": "Formal banking transactions are on record, supporting verifiable financial history for the assessment.",
  };
  return desc[reason] || "This factor contributed positively to the overall entitlement assessment.";
};

const generateReportId = (scoringDate, farmerId) => {
  if (!scoringDate) return "YNK-000000";
  const d = new Date(scoringDate);
  const y = d.getFullYear().toString().slice(-2);
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const hash = farmerId ? farmerId.slice(0, 4).toUpperCase() : "0000";
  return `YNK-${y}${m}${day}-${h}${hash}`;
};

const generateSummary = (scoreData) => {
  const score = scoreData.yogyank_score_base;
  const adj = scoreData.policy_adjustment_points;
  const postPolicyScore = scoreData.yogyank_score_final;
  const policyText = adj > 0
    ? ` Separately, the demo policy layer applies +${adj} points, yielding a post-policy score of ${postPolicyScore}.`
    : adj < 0
      ? ` Separately, the demo policy layer applies ${adj} points, yielding a post-policy score of ${postPolicyScore}.`
      : " No policy adjustment was applied.";
  if (score >= 750) {
    return `This is a strong entitlement outcome. The farmer's profile demonstrates stable repayment behaviour, suitable farm conditions, and no significant negative indicators.${policyText}`;
  }
  if (score >= 500) {
    return `This is a moderate entitlement outcome. The profile shows acceptable fundamentals with room for improvement in some areas.${policyText}`;
  }
  if (score >= 350) {
    return `This outcome indicates areas that require attention before full eligibility. Some credit indicators are below standard thresholds.${policyText}`;
  }
  return `This outcome is currently under review. Key indicators fall below minimum eligibility thresholds and further assessment may be needed.${policyText}`;
};

const getLimitationItems = (scoreData) => {
  const score = scoreData.yogyank_score_base;
  const items = [];
  if (score < 750) {
    items.push({
      title: "Scope for deeper formal banking integration",
      desc: "Expanding official banking transactions over time would strengthen the verifiable financial footprint."
    });
  }
  if (score < 600) {
    items.push({
      title: "Limited documented market linkage",
      desc: "Future assessments may benefit from stronger verifiable connections to formal market channels."
    });
  }
  if (score < 400) {
    items.push({
      title: "Key financial indicators below threshold",
      desc: "Core creditworthiness indicators require improvement before higher eligibility can be assigned."
    });
  }
  if (items.length === 0) {
    items.push({
      title: "No significant limitations identified",
      desc: "The profile meets or exceeds standard thresholds across all evaluated dimensions."
    });
  }
  return items;
};

const EntitlementReport = ({ user }) => {
  const { farmerId } = useParams();
  const navigate = useNavigate();
  const [scoreData, setScoreData] = useState(null);
  const [farmerName, setFarmerName] = useState("");
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDiagnostics, setShowDiagnostics] = useState(false);

  useEffect(() => {
    fetchEntitlementScore();
  }, [farmerId]);

  const fetchEntitlementScore = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const [scoreRes, farmerRes] = await Promise.all([
        axios.get(`${API}/yogyank/score/${farmerId}`, { headers }),
        axios.get(`${API}/farmers/${farmerId}`, { headers }),
      ]);
      setScoreData(scoreRes.data);
      setFarmerName(farmerRes.data.farmer_name || "");
    } catch (err) {
      if (err.response?.data?.scoring_status === "validation_error") {
        setError({
          type: "validation",
          missing: err.response.data.missing_fields || [],
        });
      } else {
        setError({ type: "generic", message: err.response?.data?.detail || "Failed to generate entitlement score" });
      }
      toast.error("Scoring failed");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#f5f7fa" }}>
        <div className="flex flex-col items-center gap-5" data-testid="yogyank-scoring-loader">
          <img
            src="/yogyank-loader.webp"
            alt="Yogyank Scoring"
            style={{ width: 120, height: 120, objectFit: "contain" }}
          />
          <div className="text-center">
            <p className="text-base font-semibold" style={{ color: "#2d5a27", fontFamily: "Inter, sans-serif" }}>
              Generating Entitlement Score
            </p>
            <p className="text-sm text-gray-400 mt-1" style={{ fontFamily: "Inter, sans-serif" }}>
              Running Yogyank scoring engine...
            </p>
          </div>
        </div>
      </div>
    );
  }

  const diagnostics = scoreData?.diagnostics || [];
  const reportId = scoreData ? generateReportId(scoreData.scoring_date, farmerId) : "";
  const bandStyle = scoreData ? getBandStyle(scoreData.score_band) : {};

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/entitlement-report/${farmerId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Entitlement_Report_${(farmerName || "farmer").replace(/\s+/g, "_")}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded successfully");
    } catch (err) {
      toast.error("Failed to generate PDF");
    } finally {
      setPdfLoading(false);
    }
  };
  const helpedReasons = (scoreData?.entitlement_reasons || []).map(cleanReasonText);
  const limitationItems = scoreData ? getLimitationItems(scoreData) : [];
  const policyReasons = scoreData?.policy_reasons || [];

  return (
    <div className="min-h-screen" style={{ background: "#f5f7fa", fontFamily: "Inter, sans-serif" }}>
      {/* Nav */}
      <nav className="bg-white border-b" style={{ borderColor: "#e5e7eb" }}>
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-button" className="text-gray-500 hover:text-gray-800 -ml-2">
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              Back
            </Button>
            <img src="/saakhsetu-logo.png" alt="SaakhSetu" style={{ height: "28px" }} data-testid="report-saakhsetu-logo" />
          </div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-6 lg:px-8 py-8">
        {/* ── Header ── */}
        <div className="flex items-start justify-between mb-8" data-testid="report-title-area">
          <div>
            <div className="flex items-baseline gap-2.5">
              <img src="/yogyank-logo.png" alt="Yogyank" style={{ height: "26px", position: "relative", top: "4px" }} data-testid="report-yogyank-logo" />
              <h1 className="text-2xl font-bold" style={{ fontFamily: "Space Grotesk, sans-serif", color: "#111827", letterSpacing: "-0.3px" }} data-testid="report-title">
                Entitlement Report
              </h1>
            </div>
            <p className="text-xs text-gray-400 mt-1">Generated in SaakhSetu by Arbix AI Solutions</p>
            {farmerName && <p className="text-sm text-gray-600 mt-1.5">Farmer: {farmerName}</p>}
          </div>
          {reportId && (
            <div className="text-right flex-shrink-0" data-testid="report-id-area">
              <span className="text-xs text-gray-400">Report ID</span>
              <p className="text-sm font-mono text-gray-700 mt-0.5" data-testid="report-id">{reportId}</p>
            </div>
          )}
        </div>

        {/* ── Error state ── */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-5 mb-8" data-testid="score-error-card">
            <div className="flex items-center text-red-800 font-semibold mb-2">
              <AlertTriangle className="h-4 w-4 mr-2" />
              {error.type === "validation" ? "Missing Required Fields" : "Scoring Error"}
            </div>
            {error.type === "validation" ? (
              <ul className="list-disc list-inside space-y-1 text-sm text-red-700">
                {error.missing.map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            ) : (
              <p className="text-sm text-red-700">{error.message}</p>
            )}
          </div>
        )}

        {/* ── Score data ── */}
        {scoreData && scoreData.scoring_status === "success" && (
          <div className="space-y-6">

            {/* ────── Section 1: Top Summary Row (3 cards) ────── */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="summary-row">
              {/* Card 1: Entitlement Score */}
              <div className="bg-white rounded-xl border p-5" style={{ borderColor: "#e5e7eb" }} data-testid="entitlement-score-card">
                <div className="flex items-center gap-1.5 mb-3">
                  <BarChart3 className="h-4 w-4 text-gray-400" />
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Entitlement Score</span>
                </div>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-5xl font-bold" style={{ fontFamily: "Space Grotesk, sans-serif", color: getScoreColor(scoreData.yogyank_score_base), lineHeight: 1 }} data-testid="entitlement-score">
                    {scoreData.yogyank_score_base}
                  </span>
                  <span className="text-sm text-gray-400">of 1000</span>
                </div>
                <p className="text-xs text-gray-400 mt-2">Model Output</p>
              </div>

              {/* Card 2: Eligibility Outcome */}
              <div className="bg-white rounded-xl border p-5" style={{ borderColor: "#e5e7eb" }} data-testid="eligibility-outcome-card">
                <div className="flex items-center gap-1.5 mb-3">
                  <Shield className="h-4 w-4 text-gray-400" />
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Eligibility Outcome</span>
                </div>
                <Badge
                  className="text-sm px-3 py-1.5 font-semibold border inline-flex items-center gap-1.5"
                  style={{ background: bandStyle.bg, color: bandStyle.text, borderColor: bandStyle.border }}
                  data-testid="eligibility-band"
                >
                  <Check className="h-3.5 w-3.5" />
                  {scoreData.score_band}
                </Badge>
                <p className="text-xs text-gray-400 mt-2.5">Policy Outcome</p>
              </div>

              {/* Card 3: Audit Snapshot */}
              <div className="bg-white rounded-xl border p-5" style={{ borderColor: "#e5e7eb" }} data-testid="audit-snapshot-card">
                <div className="flex items-center gap-1.5 mb-3">
                  <ClipboardList className="h-4 w-4 text-gray-400" />
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Audit Snapshot</span>
                </div>
                <div className="space-y-1.5 text-sm">
                  <div className="flex items-center gap-1.5">
                    <span className="text-gray-500 text-xs">Model</span>
                    <span className="font-mono text-gray-700 text-xs" data-testid="model-version">v{scoreData.model_version}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-gray-500 text-xs">Policy</span>
                    <span className="font-mono text-gray-700 text-xs" data-testid="policy-version">{scoreData.policy_version || "N/A"}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-gray-500 text-xs">Pipeline</span>
                    <span className="font-mono text-gray-700 text-xs" data-testid="pipeline-version">{scoreData.pipeline_version}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* ────── Section 2: Why this score was assigned ────── */}
            <div className="bg-white rounded-xl border p-6" style={{ borderColor: "#e5e7eb" }} data-testid="explainability-card">
              <h2 className="text-lg font-bold text-gray-900 mb-5" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
                Why this score was assigned
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left: What helped */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-4">What helped the score</h3>
                  <div className="space-y-4">
                    {helpedReasons.length > 0 ? helpedReasons.map((reason, i) => (
                      <div key={i} className="flex items-start gap-3" data-testid={`helped-reason-${i}`}>
                        <div className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: "#ecfdf5" }}>
                          <ArrowRight className="h-3 w-3" style={{ color: "#059669" }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900">{reason}</p>
                          <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{getReasonDescription(reason)}</p>
                          <div className="mt-1.5 h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${65 + i * 10}%`, background: "linear-gradient(90deg, #059669, #34d399)" }} />
                          </div>
                        </div>
                      </div>
                    )) : (
                      <p className="text-sm text-gray-400">No specific positive factors identified</p>
                    )}
                  </div>
                </div>

                {/* Right: What limited */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-4">What limited the score</h3>
                  <div className="space-y-4">
                    {limitationItems.map((item, i) => (
                      <div key={i} className="flex items-start gap-3" data-testid={`limited-reason-${i}`}>
                        <div className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: "#fff7ed" }}>
                          <ArrowRight className="h-3 w-3" style={{ color: "#b45309" }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900">{item.title}</p>
                          <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Summary statement */}
              <div className="mt-6 p-4 rounded-lg" style={{ background: "#f9fafb", borderLeft: "3px solid #d1d5db" }} data-testid="score-summary">
                <p className="text-sm text-gray-600 leading-relaxed">{generateSummary(scoreData)}</p>
              </div>
            </div>

            {/* ────── Section 3: Model Trace + Policy Trace ────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Model Trace */}
              <div className="bg-white rounded-xl border p-5" style={{ borderColor: "#e5e7eb" }} data-testid="model-trace-card">
                <h2 className="text-base font-bold text-gray-900 mb-4" style={{ fontFamily: "Space Grotesk, sans-serif" }}>Model Trace</h2>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Base Score</span>
                    <span className="text-lg font-bold" style={{ fontFamily: "Space Grotesk, sans-serif", color: getScoreColor(scoreData.yogyank_score_base) }} data-testid="base-score">
                      {scoreData.yogyank_score_base}
                    </span>
                  </div>

                  <div className="pt-2 border-t" style={{ borderColor: "#f3f4f6" }}>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Top Model Factors</p>
                    <div className="space-y-2">
                      {helpedReasons.slice(0, 3).map((reason, i) => (
                        <div key={i} className="flex items-center gap-2" data-testid={`model-factor-${i}`}>
                          <BarChart3 className="h-3 w-3 text-gray-300 flex-shrink-0" />
                          <span className="text-xs text-gray-700 flex-1 truncate">{reason}</span>
                          <div className="w-20 h-1.5 rounded-full bg-gray-100 overflow-hidden flex-shrink-0">
                            <div className="h-full rounded-full" style={{ width: `${70 + i * 8}%`, background: "linear-gradient(90deg, #059669, #34d399)" }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="pt-3 border-t space-y-1.5" style={{ borderColor: "#f3f4f6" }}>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400">Model Version</span>
                      <span className="font-mono text-gray-600">{scoreData.model_version}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400">Pipeline Version</span>
                      <span className="font-mono text-gray-600">{scoreData.pipeline_version}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Policy Trace */}
              <div className="bg-white rounded-xl border p-5" style={{ borderColor: "#e5e7eb" }} data-testid="policy-trace-card">
                <h2 className="text-base font-bold text-gray-900 mb-4" style={{ fontFamily: "Space Grotesk, sans-serif" }}>Policy Trace</h2>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Policy Adjustment</span>
                    <span
                      className="text-lg font-bold"
                      style={{ fontFamily: "Space Grotesk, sans-serif", color: scoreData.policy_adjustment_points > 0 ? "#059669" : scoreData.policy_adjustment_points < 0 ? "#dc2626" : "#6b7280" }}
                      data-testid="policy-adjustment"
                    >
                      {scoreData.policy_adjustment_points > 0 ? "+" : ""}{scoreData.policy_adjustment_points} pts
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Post-policy Score</span>
                    <span className="text-lg font-bold text-gray-800" style={{ fontFamily: "Space Grotesk, sans-serif" }} data-testid="post-policy-score">
                      {scoreData.yogyank_score_final}
                    </span>
                  </div>

                  <div className="pt-2 border-t" style={{ borderColor: "#f3f4f6" }}>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Policy Rule Outcome</p>
                    {policyReasons.length > 0 ? (
                      <ul className="space-y-1">
                        {policyReasons.map((r, i) => (
                          <li key={i} className="text-xs text-gray-700 flex items-start gap-1.5" data-testid={`policy-reason-${i}`}>
                            <span className="text-green-500 mt-px">+</span>
                            <span>{r}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs text-gray-400">No adjustments applied (system-generated)</p>
                    )}
                  </div>

                  <div className="pt-3 border-t space-y-1.5" style={{ borderColor: "#f3f4f6" }}>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400">Model Version</span>
                      <span className="font-mono text-gray-600">v{scoreData.model_version}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400">Policy Version</span>
                      <span className="font-mono text-gray-600">{scoreData.policy_version || "N/A"}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400">Pipeline Version</span>
                      <span className="font-mono text-gray-600">{scoreData.pipeline_version}</span>
                    </div>
                  </div>

                  {/* Diagnostics toggle */}
                  {diagnostics.length > 0 && (
                    <div className="pt-2 border-t" style={{ borderColor: "#f3f4f6" }}>
                      <button
                        onClick={() => setShowDiagnostics(!showDiagnostics)}
                        className="flex items-center text-xs text-gray-400 hover:text-gray-600 transition-colors"
                        data-testid="diagnostics-toggle"
                      >
                        {showDiagnostics ? <ChevronUp className="h-3 w-3 mr-1" /> : <ChevronDown className="h-3 w-3 mr-1" />}
                        Internal Diagnostics ({diagnostics.length})
                      </button>
                      {showDiagnostics && (
                        <div className="mt-2 space-y-1">
                          {diagnostics.map((w, i) => (
                            <div key={i} className="text-xs text-gray-400 bg-gray-50 px-2 py-1 rounded font-mono" data-testid={`diagnostic-${i}`}>{w}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ────── Section 4: Audit & Technical Trace ────── */}
            <div className="bg-white rounded-xl border p-5" style={{ borderColor: "#e5e7eb" }} data-testid="audit-trace-card">
              <div className="flex items-start justify-between mb-4">
                <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
                  Audit &amp; Technical Trace
                </h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadPdf}
                  disabled={pdfLoading}
                  className="text-xs"
                  data-testid="generate-pdf-button"
                >
                  {pdfLoading ? (
                    <span className="flex items-center gap-1.5">
                      <img src="/saakhsetu-loader.webp" alt="" style={{ width: 14, height: 14 }} />
                      Generating PDF...
                    </span>
                  ) : (
                    <>
                      <FileText className="h-3.5 w-3.5 mr-1.5" />
                      Generate PDF
                    </>
                  )}
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                <div className="flex justify-between py-1 border-b" style={{ borderColor: "#f3f4f6" }}>
                  <span className="text-gray-500 text-xs">Status</span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-700" data-testid="scoring-date">
                      {scoreData.scoring_date ? new Date(scoreData.scoring_date).toLocaleString("en-IN", { day: "numeric", month: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "N/A"}
                    </span>
                    <span className="text-xs text-gray-400">(system-generated)</span>
                  </div>
                </div>
                <div className="flex justify-between py-1 border-b" style={{ borderColor: "#f3f4f6" }}>
                  <span className="text-gray-500 text-xs">Model Version</span>
                  <span className="font-mono text-gray-700 text-xs">v{scoreData.model_version}</span>
                </div>
                <div className="flex justify-between py-1 border-b" style={{ borderColor: "#f3f4f6" }}>
                  <span className="text-gray-500 text-xs">Assessed</span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-700">
                      {scoreData.scoring_date ? new Date(scoreData.scoring_date).toLocaleString("en-IN", { day: "numeric", month: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "N/A"}
                    </span>
                    <span className="text-xs text-gray-400">(system-generated)</span>
                  </div>
                </div>
                <div className="flex justify-between py-1 border-b" style={{ borderColor: "#f3f4f6" }}>
                  <span className="text-gray-500 text-xs">Policy Version</span>
                  <span className="font-mono text-gray-700 text-xs">{scoreData.policy_version || "N/A"}</span>
                </div>
                <div className="flex justify-between py-1 border-b" style={{ borderColor: "#f3f4f6" }}>
                  <span className="text-gray-500 text-xs">Report ID</span>
                  <span className="font-mono text-gray-700 text-xs">{reportId}</span>
                </div>
                <div className="flex justify-between py-1 border-b" style={{ borderColor: "#f3f4f6" }}>
                  <span className="text-gray-500 text-xs">Pipeline Version</span>
                  <span className="font-mono text-gray-700 text-xs">{scoreData.pipeline_version}</span>
                </div>
              </div>
            </div>

          </div>
        )}

        {/* Validation error from API */}
        {scoreData && scoreData.scoring_status === "validation_error" && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-5" data-testid="validation-error-card">
            <div className="flex items-center text-red-800 font-semibold mb-2">
              <AlertTriangle className="h-4 w-4 mr-2" />
              Missing Required Fields
            </div>
            <ul className="list-disc list-inside space-y-1 text-sm text-red-700">
              {(scoreData.missing_fields || []).map((f, i) => <li key={i}>{f}</li>)}
            </ul>
          </div>
        )}

        {/* Incomplete profile */}
        {scoreData && scoreData.scoring_status === "incomplete_profile" && (
          <div className="bg-white rounded-xl border p-6" style={{ borderColor: "#e5e7eb" }} data-testid="incomplete-profile-card">
            <div className="flex items-start gap-3 mb-4">
              <div className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "#fffbeb" }}>
                <AlertTriangle className="h-4 w-4" style={{ color: "#b45309" }} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
                  Profile Incomplete for Scoring
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {scoreData.populated_fields} of {scoreData.total_fields} data fields are populated ({scoreData.completeness_pct}%).
                  The profile needs additional reference data before a reliable entitlement score can be generated.
                </p>
              </div>
            </div>

            {(scoreData.missing_reference_fields || []).length > 0 && (
              <div className="mb-4 p-4 rounded-lg" style={{ background: "#fffbeb", border: "1px solid #fcd34d" }}>
                <p className="text-sm font-semibold text-gray-800 mb-2">Missing reference data:</p>
                <div className="flex flex-wrap gap-2">
                  {scoreData.missing_reference_fields.map((f, i) => (
                    <span key={i} className="text-xs font-medium px-2 py-1 rounded-full" style={{ background: "#fef3c7", color: "#92400e" }}>
                      {f}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {(scoreData.recommendations || []).length > 0 && (
              <div>
                <p className="text-sm font-semibold text-gray-800 mb-2">To generate a score:</p>
                <ol className="space-y-1.5">
                  {scoreData.recommendations.map((r, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: "#f3f4f6", color: "#4b5563" }}>
                        {i + 1}
                      </span>
                      {r}
                    </li>
                  ))}
                </ol>
              </div>
            )}

            <div className="mt-5 pt-4 border-t" style={{ borderColor: "#f3f4f6" }}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(-1)}
                data-testid="back-to-report-button"
              >
                <ArrowLeft className="h-4 w-4 mr-1.5" />
                Back to Farmer Report
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default EntitlementReport;
